import React, { useEffect, useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  DRIVER_GUIDE_LANGUAGES,
  listDriverGuideVideos,
  saveDriverGuideVideos,
} from "../../../services/driverGuideVideoApi";

// Two YouTube links per language: the guide to the driver's trip message, and the guide to
// installing and using the Driver App. Manage Bookings adds the message guide in the language staff
// pick under Driver WhatsApp, and lists every Driver App guide so the driver picks their own.
export default function DriverGuideVideosManager() {
  const [links, setLinks] = useState({});
  const [appLinks, setAppLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = (rows) => {
    setLinks(Object.fromEntries(rows.map((row) => [row.language, row.youtubeUrl || ""])));
    setAppLinks(Object.fromEntries(rows.map((row) => [row.language, row.appYoutubeUrl || ""])));
  };

  useEffect(() => {
    listDriverGuideVideos()
      .then(apply)
      .catch(() => toast.error("Could not load the driver guide videos"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      apply(await saveDriverGuideVideos(
        DRIVER_GUIDE_LANGUAGES.map(({ code }) => ({
          language: code,
          youtubeUrl: (links[code] || "").trim(),
          appYoutubeUrl: (appLinks[code] || "").trim(),
        })),
      ));
      toast.success("Driver guide videos saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the driver guide videos");
    } finally {
      setSaving(false);
    }
  };

  const linkFields = (values, setValues, idPrefix) =>
    DRIVER_GUIDE_LANGUAGES.map(({ code, label }) => (
      <div key={code}>
        <Form.Label htmlFor={`${idPrefix}-${code}`}>{label}</Form.Label>
        <Form.Control
          id={`${idPrefix}-${code}`}
          value={values[code] || ""}
          placeholder="https://youtu.be/..."
          onChange={(e) => setValues((prev) => ({ ...prev, [code]: e.target.value }))}
        />
        {values[code] && /^https?:\/\//i.test(values[code].trim()) && (
          <a href={values[code].trim()} target="_blank" rel="noreferrer" className="small">Open video</a>
        )}
      </div>
    ));

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Driver Guide Videos</div>
        <div className="small text-muted">
          YouTube links in each language, added to the Copy Driver Message in Manage Bookings: the message video in
          the language picked under Driver WhatsApp, and every Driver App video so the driver opens their own.
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-muted"><Spinner size="sm" className="me-2" />Loading...</div>
        ) : (
          <div className="d-flex flex-column gap-4" style={{ maxWidth: 640 }}>
            <section className="d-flex flex-column gap-3">
              <div>
                <div className="fw-semibold">Driver Message Videos</div>
                <div className="small text-muted">Explain each part of the driver's trip message.</div>
              </div>
              {linkFields(links, setLinks, "message-video")}
            </section>
            <section className="d-flex flex-column gap-3">
              <div>
                <div className="fw-semibold">Driver App Videos</div>
                <div className="small text-muted">
                  How to download, install and use the IO Trips Driver App. All of them are listed in the app part of
                  the message, each under its language name.
                </div>
              </div>
              {linkFields(appLinks, setAppLinks, "app-video")}
            </section>
            <div>
              <Button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Videos"}</Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
